import {
  ArrowBackRounded,
  CheckCircleRounded,
  CloseRounded,
  DescriptionRounded,
  HourglassEmptyRounded,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QuestionnaireEmbedFrame from "../components/questionnaires/QuestionnaireEmbedFrame";
import { FORM_USER_URL } from "../config/config";
import { questionnaireApi } from "../data/api/QuestionnaireApi";
import { authManager } from "../data/managers/AuthManager";
import {
  QuestionnaireSubmission,
  QuestionnaireUserItem,
} from "../data/models/Questionnaire/QuestionnaireModel";

const progressStatus = (submission?: QuestionnaireSubmission | null) => {
  if (!submission) return "NOT_STARTED";
  if (submission.status === "COMPLETED" || submission.finishedAt) {
    return "COMPLETED";
  }
  if (submission.data && Object.keys(submission.data).length > 0) {
    return "IN_PROGRESS";
  }
  return "NOT_STARTED";
};

export default function QuestionnairePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState<QuestionnaireUserItem | null>(null);
  const [completed, setCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activePages, setActivePages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [sessionExpiryDialog, setSessionExpiryDialog] = useState(false);
  const closeQuestionnaire = () => navigate("/dashboard/plan");
  const handleEmbedSessionExpired = useCallback(() => {
    setSessionExpiryDialog(true);
  }, []);
  const confirmSessionExpiry = useCallback(async () => {
    await authManager.logout();
    navigate("/login?session=expired", { replace: true });
  }, [navigate]);
  const renderFullScreenModal = (children: any) => (
    <Dialog
      open
      fullScreen
      onClose={closeQuestionnaire}
      PaperProps={{
        sx: {
          m: 0,
          width: "100vw",
          height: "100vh",
          maxWidth: "none",
          bgcolor: "#f5f7fb",
          backgroundImage:
            "linear-gradient(180deg, rgba(246,249,255,1) 0%, rgba(255,255,255,1) 55%, rgba(232,240,255,1) 100%)",
          overflow: "hidden",
        },
      }}
    >
      <Dialog
        open={sessionExpiryDialog}
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 460 } }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800}>
              Tu sesión venció
            </Typography>
            <Typography color="text.secondary">
              Por seguridad, el acceso al formulario ya no es válido,
              normalmente por haber estado abierto durante mucho tiempo o sin
              actividad. Tus cambios confirmados antes de este aviso quedaron
              guardados. Inicia sesión nuevamente para continuar.
            </Typography>
            <Button variant="contained" onClick={() => void confirmSessionExpiry()}>
              Entendido, iniciar sesión
            </Button>
          </Stack>
        </CardContent>
      </Dialog>
      {children}
    </Dialog>
  );

  const loadQuestionnaire = useCallback(async () => {
    if (!code) return;

    setLoading(true);
    try {
      const result = await questionnaireApi.questionnaireByCode.request({
        questionnaireCode: code,
      });

      if (result.isSuccess && result.data) {
        setItem(result.data);
        const initialSubmission = result.data.submission;
        setCurrentPage(initialSubmission?.currentPage ?? 0);
        setCompleted(progressStatus(initialSubmission) === "COMPLETED");
      } else {
        setItem(null);
      }
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    void loadQuestionnaire();
  }, [loadQuestionnaire]);

  const questionnaire = item?.questionnaire ?? null;
  const questionnaireVersions = questionnaire?.versions ?? [];
  const submissionVersion = item?.submission
    ? questionnaireVersions.find(
        (version) => version.id === String(item.submission?.questionnaireVersionId),
      ) ?? null
    : null;
  const currentVersion = submissionVersion ?? questionnaire?.activeVersion ?? null;
  const status = progressStatus(item?.submission);

  const progressPercent = useMemo(() => {
    if (completed) return 100;
    if (!activePages) return 0;
    return ((currentPage + 1) / activePages) * 100;
  }, [activePages, completed, currentPage]);

  useEffect(() => {
    if (!item?.submission) return;
    setCurrentPage(item.submission.currentPage ?? 0);
  }, [item?.submission]);

  const statusConfig = {
    NOT_STARTED: {
      label: "Aun no has iniciado este formulario",
      color: "error.main",
      icon: <HourglassEmptyRounded sx={{ color: "error.main" }} />,
    },
    IN_PROGRESS: {
      label: "Formulario en progreso",
      color: "warning.main",
      icon: <DescriptionRounded sx={{ color: "warning.main" }} />,
    },
    COMPLETED: {
      label: "Formulario completado",
      color: "success.main",
      icon: <CheckCircleRounded sx={{ color: "success.main" }} />,
    },
  }[completed ? "COMPLETED" : status];

  if (loading) {
    return renderFullScreenModal(
      <Box
        sx={{
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>,
    );
  }

  if (!questionnaire || !currentVersion) {
    return renderFullScreenModal(
      <Box
        sx={{
          minHeight: "100%",
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 640, width: "100%" }}>
          <Alert severity="warning">
            No encontramos una version activa para este formulario.
          </Alert>
          <Button
            startIcon={<ArrowBackRounded />}
            onClick={closeQuestionnaire}
            sx={{ alignSelf: "flex-start" }}
          >
            Volver
          </Button>
        </Stack>
      </Box>,
    );
  }

  if (completed) {
    return renderFullScreenModal(
      <Box
        sx={{
          minHeight: "100%",
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 900,
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <CheckCircleRounded sx={{ fontSize: 72, color: "success.main" }} />
              <Typography variant="h4" fontWeight={900}>
                Formulario completado
              </Typography>
              <Typography color="text.secondary">
                {questionnaire.name} ya fue enviado correctamente.
              </Typography>
              <Button
                variant="contained"
                onClick={closeQuestionnaire}
                startIcon={<ArrowBackRounded />}
              >
                Volver al plan
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>,
    );
  }

  return renderFullScreenModal(
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        p: { xs: 1, md: 1.5 },
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            color: "#fff",
            background:
              "linear-gradient(90deg, #051B6D 0%, #123BFF 55%, #0061FF 100%)",
            boxShadow: "0 12px 28px rgba(0,52,160,0.16)",
          }}
        >
          <CardContent sx={{ p: { xs: 1.75, md: 2.25 } }}>
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="overline" sx={{ opacity: 0.85, lineHeight: 1 }}>
                    Convisas | Formulario
                  </Typography>
                  <Typography variant="h6" fontWeight={900} lineHeight={1.05}>
                    {questionnaire.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                    {questionnaire.description ||
                      "Completa este formulario para continuar."}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={closeQuestionnaire}
                  aria-label="Cerrar formulario"
                  sx={{ color: "#fff", mt: -0.25 }}
                >
                  <CloseRounded fontSize="small" />
                </IconButton>
              </Stack>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.16)" }} />

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {statusConfig.icon}
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: statusConfig.color }}
                  >
                    {statusConfig.label}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" label={`${Math.round(progressPercent)}%`} color="secondary" />
                  {saving && <Chip size="small" label="Guardando..." color="info" />}
                </Stack>
              </Stack>

              <Box
                sx={{
                  height: 5,
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.16)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${progressPercent}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: "linear-gradient(90deg, #EAF4FF 0%, #8FD3FF 100%)",
                    transition: "width 0.2s ease",
                  }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 2,
            boxShadow: "0 10px 28px rgba(0,52,160,0.08)",
            bgcolor: "#ffffff",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", padding: '8px' }}>
            <QuestionnaireEmbedFrame
              key={currentVersion.id}
              path={FORM_USER_URL}
              mode="form"
              questionnaireCode={questionnaire.code}
              versionId={currentVersion.id}
              closable={false}
              chrome="compact"
              height="100%"
              onReady={(payload) => {
                setActivePages(Number(payload?.totalPages ?? 0));
                setCurrentPage(Number(payload?.currentPage ?? 0));
              }}
              onProgress={(payload) => {
                setSaving(true);
                if (typeof payload?.currentPage === "number") {
                  setCurrentPage(payload.currentPage);
                }
                if (typeof payload?.totalPages === "number") {
                  setActivePages(payload.totalPages);
                }
              }}
              onSaved={(payload) => {
                setSaving(false);
                if (typeof payload?.currentPage === "number") {
                  setCurrentPage(payload.currentPage);
                }
                if (payload?.submission) {
                  setItem((prev) =>
                    prev
                      ? {
                          ...prev,
                          submission: payload.submission,
                        }
                      : prev,
                  );
                  setCompleted(
                    progressStatus(payload.submission) === "COMPLETED",
                  );
                }
              }}
              onSubmitted={(payload) => {
                setSaving(false);
                setCompleted(true);
                if (typeof payload?.submission?.currentPage === "number") {
                  setCurrentPage(payload.submission.currentPage);
                }
                if (payload?.submission) {
                  setItem((prev) =>
                    prev
                      ? {
                          ...prev,
                          submission: payload.submission,
                        }
                      : prev,
                  );
                }
              }}
              onClose={() => {
                setSaving(false);
                closeQuestionnaire();
              }}
              onSessionExpired={handleEmbedSessionExpired}
            />
          </Box>
        </Card>
      </Stack>
    </Box>
  );
}
